import * as React from 'react';

import { translate } from '~l10n/client';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { SmallButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import TextInput from '~shared/components/TextInput';
import { toast } from '~shared/components/Toast';
import { useSelectedTeamQuery } from '~teacher/components/Account/hooks';
import { useRenameTeam } from '~teacher/hook/team/useRenameTeam';

interface RenameTeamDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RenameTeamDialog({ isOpen, onClose }: RenameTeamDialogProps) {
  const selectedTeamQuery = useSelectedTeamQuery();
  const useRenameTeamMutation = useRenameTeam();
  const [newTeamName, setNewTeamName] = React.useState(
    selectedTeamQuery?.data?.displayName ?? ''
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const handleRenamingTeam = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsLoading(true);

    useRenameTeamMutation.mutate(
      { teamId: selectedTeamQuery?.data?.id, name: newTeamName },
      {
        onSuccess: () => {
          onClose();

          // Analytics
          sendTeacherAmplitudeEvent(EventTypes.TeamsRenameTeam, {
            team_id: selectedTeamQuery?.data?.id,
          });
        },
        onError: () => {
          toast.critical('Unknown Error. Please try again later.');
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <Dialog
      id="invite-members-dialog"
      open={isOpen}
      onClose={() => {
        onClose();
        setNewTeamName(selectedTeamQuery.data?.displayName ?? '');
      }}
      title={translate('Rename Team')}
    >
      <form className="grid gap-4 pt-5" onSubmit={handleRenamingTeam}>
        <Divider />
        <label className="flex flex-col gap-3 font-sans text-base font-400 leading-8">
          {translate('Name')}
          <div className="flex w-full flex-col sm:flex-row gap-2">
            <TextInput
              className="w-full flex-1"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              maxLength={128}
            />
            <div className="w-auto flex-shrink-0 ml-auto">
              <SmallButton
                type="submit"
                appearance="primary"
                isLoading={isLoading}
                isDisabled={isLoading || newTeamName.trim().length === 0}
              >
                {translate('Save')}
              </SmallButton>
            </div>
          </div>
        </label>
      </form>
    </Dialog>
  );
}
